import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, X, ArrowRight, RotateCcw, Trophy, ArrowLeft } from "lucide-react";
import Topbar from "../components/Topbar";
import { getQuiz, courses } from "../data/mockData";

export default function Quiz() {
    const { courseId } = useParams();
    const quiz = getQuiz(courseId);
    const course = courses.find((c) => c.id === courseId) || courses[0];

    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [revealed, setRevealed] = useState(false);
    const [done, setDone] = useState(false);

    const q = quiz.questions[step];
    const total = quiz.questions.length;

    const submit = () => {
        if (answers[q.id] === undefined) return;
        setRevealed(true);
    };

    const next = () => {
        setRevealed(false);
        if (step < total - 1) setStep(step + 1);
        else setDone(true);
    };

    const restart = () => {
        setStep(0);
        setAnswers({});
        setRevealed(false);
        setDone(false);
    };

    const score = quiz.questions.reduce((acc, qq) => acc + (answers[qq.id] === qq.correctIndex ? 1 : 0), 0);
    const percent = Math.round((score / total) * 100);
    const passed = percent >= 60;

    return (
        <div className="min-h-screen bg-ink-900 text-white">
            <Topbar searchPlaceholder="Search..." />
            <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
                <Link to={`/learn/${courseId}`} data-testid="back-to-lesson" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-8">
                    <ArrowLeft className="w-4 h-4" /> Back to lesson
                </Link>

                {!done ? (
                    <>
                        {/* Progress */}
                        <div className="flex items-center justify-between mb-8">
                            <div className="text-xs uppercase tracking-widest text-grape font-semibold">{quiz.title}</div>
                            <div className="text-sm text-gray-400">Question <span className="text-white font-bold">{step + 1}</span> of {total}</div>
                        </div>
                        <div className="h-1.5 rounded-full bg-ink-700 overflow-hidden mb-12">
                            <div className="h-full rounded-full bg-gradient-to-r from-grape to-lime transition-all" style={{ width: `${((step + 1) / total) * 100}%` }} />
                        </div>

                        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tight leading-tight mb-10 fade-up">{q.text}</h1>

                        <div className="space-y-3">
                            {q.options.map((opt, idx) => {
                                const selected = answers[q.id] === idx;
                                const isCorrect = idx === q.correctIndex;
                                let style = "border-white/10 hover:border-white/30";
                                if (revealed) {
                                    if (isCorrect) style = "border-lime bg-lime/10";
                                    else if (selected && !isCorrect) style = "border-red-500 bg-red-500/10";
                                    else style = "border-white/5 opacity-60";
                                } else if (selected) {
                                    style = "border-lime bg-lime/5";
                                }
                                return (
                                    <button
                                        key={idx}
                                        disabled={revealed}
                                        onClick={() => setAnswers({ ...answers, [q.id]: idx })}
                                        data-testid={`option-${idx}`}
                                        className={`w-full text-left px-6 py-5 rounded-2xl border-2 bg-ink-700 transition-all flex items-center justify-between gap-4 ${style}`}
                                    >
                                        <span className="flex items-center gap-4">
                                            <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-bold ${selected || (revealed && isCorrect) ? "border-lime text-lime" : "border-white/20 text-gray-400"}`}>
                                                {String.fromCharCode(65 + idx)}
                                            </span>
                                            <span className="font-medium">{opt}</span>
                                        </span>
                                        {revealed && isCorrect && <Check className="w-5 h-5 text-lime" />}
                                        {revealed && selected && !isCorrect && <X className="w-5 h-5 text-red-500" />}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-10 flex justify-end">
                            {!revealed ? (
                                <button
                                    onClick={submit}
                                    disabled={answers[q.id] === undefined}
                                    data-testid="submit-answer"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-ink-900 font-bold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-lime-hover transition-all"
                                >
                                    Submit answer <ArrowRight className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={next}
                                    data-testid="next-question"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all"
                                >
                                    {step < total - 1 ? "Next question" : "See results"} <ArrowRight className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="text-center pt-10 fade-up">
                        <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center ${passed ? "bg-lime/15" : "bg-red-500/15"}`}>
                            <Trophy className={`w-10 h-10 ${passed ? "text-lime" : "text-red-400"}`} />
                        </div>
                        <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mt-6">
                            {passed ? "Nice work!" : "Keep going."}
                        </h1>
                        <p className="text-gray-400 mt-3 max-w-md mx-auto">
                            {passed ? "You aced this round. Keep that streak alive." : "Just a few more reps and you'll have it."}
                        </p>
                        <div className="mt-10 inline-flex items-center gap-12 px-10 py-6 rounded-3xl bg-ink-700 border border-white/5">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gray-500">Score</div>
                                <div className="font-display font-black text-4xl mt-1">{score}<span className="text-gray-500">/{total}</span></div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gray-500">Percent</div>
                                <div className={`font-display font-black text-4xl mt-1 ${passed ? "text-lime" : "text-red-400"}`}>{percent}%</div>
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-widest text-gray-500">Status</div>
                                <div className="font-display font-black text-4xl mt-1">{passed ? "Pass" : "Retry"}</div>
                            </div>
                        </div>

                        <div className="mt-10 flex justify-center gap-3">
                            <button onClick={restart} data-testid="retake-quiz" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white font-semibold hover:bg-white/5 transition-colors">
                                <RotateCcw className="w-4 h-4" /> Retake
                            </button>
                            <Link to={`/learn/${courseId}`} data-testid="continue-course" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all">
                                Continue course <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
